import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import MovieCard from "@/components/feature/MovieCard";
import Pagination from "@/components/base/Pagination";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";
import { filterMovies, getPopular, PAGE_SIZE } from "@/lib/catalog";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useReveal } from "@/hooks/useReveal";

export default function Search() {
  const [params, setParams] = useSearchParams();
  const keyword = params.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const page = Number(params.get("page") ?? "1") || 1;

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  usePageSeo({
    title: keyword ? `“${keyword}”搜索结果 - 蜜桃成熟时电影` : "搜索电影 - 蜜桃成熟时电影",
    description: "搜索电影资源与剧情介绍，输入片名、演员或题材关键词，快速找到目标电影。",
    keywords: "电影搜索,搜索电影,电影资源搜索,蜜桃成熟时电影",
  });

  const result = useMemo(() => filterMovies({ keyword }), [keyword]);
  const totalPages = Math.max(1, Math.ceil(result.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = result.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);
  const hot = getPopular(4);

  useReveal([keyword, current]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const k = input.trim();
    setParams(k ? { q: k } : {});
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 pt-6 md:pt-8">
        <Breadcrumb items={[{ label: "搜索" }]} />

        <div className="mt-5 md:mt-6 mb-8">
          <h1 className="font-heading font-black text-3xl md:text-4xl text-foreground-950">搜索电影</h1>
          <p className="mt-2 text-sm md:text-base text-foreground-500">
            通过片名、导演或演员关键词，快速找到目标电影资源。
          </p>
        </div>

        <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 max-w-3xl mb-8">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-secondary-400 text-lg"></i>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入电影名称、演员或题材"
              className="w-full h-12 pl-11 pr-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200/60 transition"
            />
          </div>
          <button
            type="submit"
            className="h-12 px-8 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>

        {keyword ? (
          <p className="text-sm text-foreground-600 mb-6">
            关键词 <span className="font-semibold text-primary-600">“{keyword}”</span> 共匹配到{" "}
            <span className="font-semibold text-primary-600">{result.length}</span> 部影片
          </p>
        ) : (
          <p className="text-sm text-foreground-500 mb-6">输入关键词开始搜索，下方为热门推荐。</p>
        )}

        {keyword && result.length === 0 ? (
          <div className="space-y-10">
            <EmptyState
              icon="ri-search-eye-line"
              title="未找到相关电影"
              desc={`没有找到与“${keyword}”相关的电影，试试更换关键词，或看看下面的热门推荐。`}
              action={
                <Link
                  to="/category"
                  className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-apps-2-line"></i>
                  浏览全部分类
                </Link>
              }
            />
            <div>
              <h2 className="font-heading font-bold text-xl text-foreground-950 mb-5">热门电影推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-5">
                {hot.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
              {(keyword ? pageItems : hot).map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
            {keyword && (
              <Pagination
                page={current}
                totalPages={totalPages}
                onChange={(p) => {
                  setParams({ q: keyword, page: String(p) });
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
              />
            )}
          </>
        )}
      </div>
    </SiteLayout>
  );
}